import React, { useState, useEffect } from "react";
import { Link, useLocation } from "wouter";
import { X, Check, Zap, MessageCircle, Send, ShieldCheck, Clock, CheckCircle2, Menu } from "lucide-react";
import { toast } from "sonner";
const confetti = (opts: Record<string, unknown>) => {
  import("canvas-confetti").then(m => m.default(opts as any));
};
import { trackConversion, trackEvent, newLeadEventId, getOpenAIRefs } from "@/lib/analytics";
import { tercihPaneliniAc } from "@/lib/cerez_izni";
import { talepKaynagi, TALEP_HATA_MESAJI } from "@/lib/talep_kaynagi";
import { useRoas } from "@/contexts/RoasContext";
import { trpc } from "@/lib/trpc";
import { MegaMenuNav, MegaMenuMobile } from "./MegaMenu";
import OnuncuYilRozeti from "./OnuncuYilRozeti";
import EkIcerikOto from "./EkIcerik";

interface LayoutProps {
  children: React.ReactNode;
}

export default function Layout({ children }: LayoutProps) {
  const [location] = useLocation();
  const [isOfferOpen, setIsOfferOpen] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  // Sayfa geçişlerinde üstteki fıstık yeşili loading bar animasyonunu tetikliyoruz
  useEffect(() => {
    setIsLoading(true);
    setLoadingProgress(15);

    const timer1 = setTimeout(() => setLoadingProgress(45), 80);
    const timer2 = setTimeout(() => setLoadingProgress(75), 180);
    const timer3 = setTimeout(() => setLoadingProgress(90), 300);
    const timer4 = setTimeout(() => {
      setLoadingProgress(100);
      setTimeout(() => {
        setIsLoading(false);
        setLoadingProgress(0);
      }, 200);
    }, 450);

    return () => {
      clearTimeout(timer1);
      clearTimeout(timer2);
      clearTimeout(timer3);
      clearTimeout(timer4);
    };
  }, [location]);
  const [isWhatsAppOpen, setIsWhatsAppOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);


  const [whatsappMessage, setWhatsAppMessage] = useState("");

  const [clientName, setClientName] = useState("");
  const [clientEmail, setClientEmail] = useState("");
  const [clientPhone, setClientPhone] = useState("");
  const [clientPlatform, setClientPlatform] = useState("Shopify");
  const [clientMessage, setClientMessage] = useState("");
  const [isSuccess, setIsSuccess] = useState(false);

  // RoasContext'ten verileri alıyoruz
  const { roasData } = useRoas();

  // Kullanıcı ROAS hesaplayıcıyı kullandıysa mesajı dinamik olarak güncelliyoruz
  useEffect(() => {
    if (roasData.hasCalculated) {
      const calculatedRevenue = roasData.budget * roasData.roas;
      setWhatsAppMessage(
        `Merhaba Dijipo! Web sitenizdeki ROAS hesaplama aracını kullandım. \n\n- Aylık Reklam Bütçemiz: ₺${roasData.budget.toLocaleString("tr-TR")}\n- Hedef ROAS Oranımız: ${roasData.roas.toFixed(1)}x\n- Ortalama Sepet Tutarımız: ₺${roasData.aov.toLocaleString("tr-TR")}\n- Hedeflenen Aylık Ciro: ₺${calculatedRevenue.toLocaleString("tr-TR")}\n\nBu hedefler doğrultusunda e-ticaret sitemizi büyütmek için profesyonel destek almak istiyoruz.`
      );
    } else {
      setWhatsAppMessage("");
    }
  }, [roasData.budget, roasData.roas, roasData.aov, roasData.hasCalculated]);

  // Fıstık yeşili ve marka renklerinde konfeti patlatma fonksiyonu
  const triggerConfetti = () => {
    const duration = 2 * 1000;
    const animationEnd = Date.now() + duration;
    const defaults = { startVelocity: 25, spread: 360, ticks: 50, zIndex: 100, colors: ["#9EFF00", "#000000", "#FFFFFF"] };

    const randomInRange = (min: number, max: number) => Math.random() * (max - min) + min;

    const interval = setInterval(function() {
      const timeLeft = animationEnd - Date.now();

      if (timeLeft <= 0) {
        return clearInterval(interval);
      }

      const particleCount = 40 * (timeLeft / duration);
      // Sol ve sağ köşelerden fırlatma
      confetti(Object.assign({}, defaults, { particleCount, origin: { x: randomInRange(0.1, 0.3), y: Math.random() - 0.2 } }));
      confetti(Object.assign({}, defaults, { particleCount, origin: { x: randomInRange(0.7, 0.9), y: Math.random() - 0.2 } }));
    }, 250);
  };

  const formSubmitMutation = trpc.form.submit.useMutation();

  const handleSendOffer = (e: React.FormEvent) => {
    e.preventDefault();
    if (formSubmitMutation.isPending) return;
    if (!clientName || !clientEmail) {
      toast.error("Lütfen adınızı ve e-posta adresinizi doldurun.");
      return;
    }

    // Piksel + Dönüşümler API'si tekilleştirmesi için ortak event id
    const openaiEventId = newLeadEventId();

    formSubmitMutation.mutate(
      {
        formType: "contact",
        name: clientName,
        email: clientEmail,
        phone: clientPhone || undefined,
        platform: clientPlatform || undefined,
        message: clientMessage || undefined,
        eventId: openaiEventId,
        ...getOpenAIRefs(),
        kaynak: talepKaynagi("Üst menü · Ücretsiz analiz penceresi"),
      },
      {
        onSuccess: () => {
          setIsSuccess(true);
          triggerConfetti();
          toast.success("Analiz Talebiniz Alındı!");
          // Dönüşüm takibi
          trackConversion('analysis', { platform: clientPlatform }, {
            email: clientEmail,
            phone: clientPhone,
            first_name: clientName.split(' ')[0],
            last_name: clientName.split(' ').slice(1).join(' '),
          }, openaiEventId);
        },
        // Talep kaydedilemediyse başarı gösterilmez ve dönüşüm sayılmaz; ziyaretçi düzeltip tekrar dener
        onError: () => {
          toast.error(TALEP_HATA_MESAJI);
        },
      }
    );
  };

  const handleSendWhatsApp = (e: React.FormEvent) => {
    e.preventDefault();
    const finalMessage = whatsappMessage || "Merhaba Dijipo, e-ticaret sitemiz için profesyonel destek almak istiyoruz.";
    const encodedMessage = encodeURIComponent(finalMessage);
    const whatsappUrl = `https://wa.me/905320522545?text=${encodedMessage}`;
    window.open(whatsappUrl, "_blank");
    setIsWhatsAppOpen(false);
    toast.success("WhatsApp'a yönlendiriliyorsunuz!");
    // Dönüşüm takibi
    trackEvent('contact', { event_label: 'whatsapp_chat', content_name: 'WhatsApp Destek' });

    // Canlı Fomo Tetikle
    const event = new CustomEvent("fomo-trigger", {
      detail: {
        city: "Canlı",
        brandType: "E-Ticaret Girişimi",
        actionType: "WhatsApp Destek hattından iletişim başlattı",
        icon: "crm"
      }
    });
    window.dispatchEvent(event);
  };

  const resetForm = () => {
    setIsSuccess(false);
    setClientName("");
    setClientEmail("");
    setClientPhone("");
    setClientMessage("");
  };

  return (
    <div className="min-h-screen flex flex-col bg-white text-black font-sans selection:bg-[#9EFF00] selection:text-black">
      
      {/* TOP LOADING PROGRESS BAR (saf CSS gecisi — framer-motion kaldirildi) */}
      {isLoading && (
        <div className="fixed top-0 left-0 right-0 h-[4px] bg-black z-50 overflow-hidden">
          <div
            className="h-full bg-[#9EFF00] border-r border-black transition-[width] duration-300 ease-out"
            style={{ width: `${loadingProgress}%` }}
          />
        </div>
      )}

      {/* HEADER / NAVBAR */}
      <header className="sticky top-0 z-40 relative bg-white text-black border-b-3 border-black px-4 py-3 md:px-8">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <Link href="/" className="flex items-center cursor-pointer group">
            <div className="bg-white border-3 border-black shadow-[3px_3px_0px_0px_rgba(158,255,0,1)] group-hover:-translate-y-1.5 group-hover:-translate-x-1 group-hover:scale-[1.03] group-hover:shadow-[6px_6px_0px_0px_rgba(158,255,0,1)] transition-all duration-300 ease-out px-1 overflow-hidden">
              <img src="/dijipo-logo-160.png" alt="Dijipo" width={112} height={40} className="h-9 md:h-10 w-24 md:w-28 object-cover object-center" />
            </div>
          </Link>

          
          <nav className="hidden md:flex items-center space-x-4 lg:space-x-6 font-bold text-xs lg:text-sm">
            <Link href="/kurumsal" className={`hover:text-neutral-600 transition-colors ${location === "/kurumsal" ? "underline decoration-[#9EFF00] decoration-4" : ""}`}>Kurumsal</Link>
            <Link href="/hizmetlerimiz" className={`hover:text-neutral-600 transition-colors ${location === "/hizmetlerimiz" ? "underline decoration-[#9EFF00] decoration-4" : ""}`}>Hizmetlerimiz</Link>
            <MegaMenuNav />
            <Link href="/referanslar" className={`hover:text-neutral-600 transition-colors ${location === "/referanslar" ? "underline decoration-[#9EFF00] decoration-4" : ""}`}>Referanslar</Link>
            <Link href="/fiyatlar" className={`hover:text-neutral-600 transition-colors ${location === "/fiyatlar" ? "underline decoration-[#9EFF00] decoration-4" : ""}`}>Fiyatlar</Link>
            <Link href="/randevu" className={`hover:text-neutral-600 transition-colors ${location === "/randevu" ? "underline decoration-[#9EFF00] decoration-4" : ""}`}>Randevu</Link>
          </nav>

          <div className="flex items-center space-x-3">
            <button 
              data-cta
              onClick={() => setIsOfferOpen(true)}
              className="hidden md:block bg-black text-[#9EFF00] border-2 border-black px-4 py-2 font-bold text-xs md:text-sm shadow-[3px_3px_0px_0px_rgba(158,255,0,1)] hover:translate-x-[1px] hover:translate-y-[1px] hover:shadow-[2px_2px_0px_0px_rgba(158,255,0,1)] active:scale-[0.97] active:translate-x-[3px] active:translate-y-[3px] active:shadow-none transition-all cursor-pointer"
            >
              Ücretsiz Analiz Alın
            </button>
            
            {/* Hamburger Menu for Mobile */}
            <button 
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              aria-label={isMobileMenuOpen ? "Menüyü kapat" : "Menüyü aç"}
              aria-expanded={isMobileMenuOpen}
              className="md:hidden bg-white text-black border-2 border-black p-2 shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] active:scale-[0.95] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none transition-all cursor-pointer"
            >
              {isMobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu Overlay */}
        {isMobileMenuOpen && (
            <div className="md:hidden border-t-2 border-black bg-white text-black overflow-hidden animate-[dj-menu-in_0.25s_ease-out]">
              <div className="p-6 flex flex-col space-y-4 font-bold text-sm">
                <Link 
                  href="/kurumsal" 
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={`py-2 border-b border-neutral-100 hover:text-neutral-600 transition-colors ${location === "/kurumsal" ? "text-[#9EFF00] bg-black px-3 py-1.5 border-2 border-black shadow-[2px_2px_0px_0px_rgba(0,0,0,1)]" : ""}`}
                >
                  Kurumsal
                </Link>
                <Link 
                  href="/hizmetlerimiz" 
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={`py-2 border-b border-neutral-100 hover:text-neutral-600 transition-colors ${location === "/hizmetlerimiz" ? "text-[#9EFF00] bg-black px-3 py-1.5 border-2 border-black shadow-[2px_2px_0px_0px_rgba(0,0,0,1)]" : ""}`}
                >
                  Hizmetlerimiz
                </Link>
                <MegaMenuMobile onNavigate={() => setIsMobileMenuOpen(false)} />
                <Link 
                  href="/referanslar" 
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={`py-2 border-b border-neutral-100 hover:text-neutral-600 transition-colors ${location === "/referanslar" ? "text-[#9EFF00] bg-black px-3 py-1.5 border-2 border-black shadow-[2px_2px_0px_0px_rgba(0,0,0,1)]" : ""}`}
                >
                  Referanslar
                </Link>
                <Link 
                  href="/fiyatlar" 
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={`py-2 border-b border-neutral-100 hover:text-neutral-600 transition-colors ${location === "/fiyatlar" ? "text-[#9EFF00] bg-black px-3 py-1.5 border-2 border-black shadow-[2px_2px_0px_0px_rgba(0,0,0,1)]" : ""}`}
                >
                  Fiyatlar
                </Link>
                <Link 
                  href="/randevu" 
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={`py-2 border-b border-neutral-100 hover:text-neutral-600 transition-colors ${location === "/randevu" ? "text-[#9EFF00] bg-black px-3 py-1.5 border-2 border-black shadow-[2px_2px_0px_0px_rgba(0,0,0,1)]" : ""}`}
                >
                  Randevu
                </Link>
                <button 
                  data-cta
                  onClick={() => { setIsMobileMenuOpen(false); setIsOfferOpen(true); }}
                  className="w-full bg-[#9EFF00] text-black border-2 border-black py-3 font-bold text-center text-sm shadow-[3px_3px_0px_0px_rgba(0,0,0,1)] active:translate-x-[1px] active:translate-y-[1px] active:shadow-none transition-all cursor-pointer"
                >
                  Ücretsiz Analiz Alın
                </button>
              </div>
            </div>
        )}
      </header>

      {/* SAYFA İÇERİĞİ - WITH NEO-BRUTALIST TRANSITION ANIMATIONS */}
      <main className="flex-grow overflow-hidden">
        {/* Rota degisiminde kisa giris animasyonu (CSS keyframe; JS kutuphanesi yok) */}
        <div key={location} className="animate-[dj-page-in_0.25s_cubic-bezier(0.23,1,0.32,1)]">
          {children}
          {/* İnce içerikli sayfalar için yol bazlı ek rehber + SSS (SSR ile birebir aynı veri) */}
          <EkIcerikOto />
        </div>
      </main>

      {/* FOOTER */}
      <footer className="bg-black text-white border-t-3 border-black py-14 px-4 md:px-8 mt-auto">
        {/* Üst blok: büyük logo + tanıtım + sosyal — tam genişlik, ayırıcı çizgiyle */}
        <div className="max-w-7xl mx-auto border-b border-neutral-800 pb-10 mb-10 grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
          <div className="space-y-5">
            <img
              src="/dijipo-logo-160.png"
              alt="Dijipo Logo"
              width={80}
              height={80}
              loading="lazy"
              className="h-16 md:h-20 w-auto object-contain invert"
            />
            <p className="text-sm text-neutral-400 font-medium leading-relaxed max-w-xl">
              Dijipo, İstanbul Ataşehir merkezli bir e-ticaret reklam ajansıdır. Meta, Google ve TikTok reklam yönetimi, Shopify ve ikas mağaza kurulumu, SEO ve GEO ile pazaryeri entegrasyonu hizmetleri sunar.
            </p>
          </div>
          <div className="space-y-4 lg:text-right">
            <div className="flex lg:justify-end items-center gap-4">
              <OnuncuYilRozeti boyut={84} koyu />
              <div className="text-xs space-y-1 lg:text-left">
                <p className="font-mono text-[10px] font-bold tracking-widest text-[#9EFF00]">2016'DAN BERİ</p>
                <p className="font-bold text-white">Sektörde 10. yılımız · 200+ proje · 4,8M $+ yönetilen reklam bütçesi</p>
              </div>
            </div>
            <div className="text-xs space-y-1">
              <p className="font-bold text-white">Resmi İş Ortakları</p>
              <p className="font-mono text-neutral-400">ikas · Shopify · Google Partner · Meta Business Partner</p>
            </div>
            <div className="flex lg:justify-end gap-3">
              <a href="https://www.instagram.com/dijitalpazarlamaofisi" target="_blank" rel="noopener" className="border-2 border-neutral-700 px-4 py-2 text-xs font-bold text-neutral-300 hover:border-[#9EFF00] hover:text-[#9EFF00] transition-colors">Instagram</a>
              <a href="https://www.youtube.com/@dijipoagency" target="_blank" rel="noopener" className="border-2 border-neutral-700 px-4 py-2 text-xs font-bold text-neutral-300 hover:border-[#9EFF00] hover:text-[#9EFF00] transition-colors">YouTube</a>
              <a href="https://www.linkedin.com/company/dijipo" target="_blank" rel="noopener" className="border-2 border-neutral-700 px-4 py-2 text-xs font-bold text-neutral-300 hover:border-[#9EFF00] hover:text-[#9EFF00] transition-colors">LinkedIn</a>
            </div>
          </div>
        </div>

        {/* Link sütunları: 5 eşit sütun, her bölüm kendi sütununda */}
        <div className="max-w-7xl mx-auto grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-8">

          <div className="space-y-3">
            <h3 className="font-bold text-sm text-[#9EFF00] border-b-2 border-[#9EFF00]/30 pb-2">HİZMETLER</h3>
            <ul className="text-xs space-y-2.5 text-neutral-400 font-semibold">
              <li><Link href="/shopify" className="hover:underline hover:text-white transition-colors">Shopify Hizmetleri</Link></li>
              <li><Link href="/ikas" className="hover:underline hover:text-white transition-colors">ikas Hizmetleri</Link></li>
              <li><Link href="/istanbul-eticaret-reklam-ajansi" className="hover:underline hover:text-white transition-colors">İstanbul E-Ticaret Reklam Ajansı</Link></li>
              <li><Link href="/hizmetlerimiz/reklam-yonetimi" className="hover:underline hover:text-white transition-colors">Reklam Yönetimi Hizmeti</Link></li>
              <li><Link href="/shopify/magaza-kurulumu" className="hover:underline hover:text-white transition-colors">Shopify Mağaza Kurulumu</Link></li>
              <li><Link href="/shopify-reklam-ajansi" className="hover:underline hover:text-white transition-colors">Shopify Reklam Yönetimi</Link></li>
              <li><Link href="/hizmetlerimiz/seo-geo" className="hover:underline hover:text-white transition-colors">GEO Hizmeti</Link></li>
              <li><Link href="/hizmetlerimiz/pazar-yeri" className="hover:underline hover:text-white transition-colors">Pazaryeri Entegrasyonları</Link></li>
              <li><Link href="/hizmetlerimiz/e-ihracat" className="hover:underline hover:text-white transition-colors">E-İhracat</Link></li>
              <li><Link href="/facebook-reklam-hesabi-kiralama" className="hover:underline hover:text-white transition-colors">Reklam Hesabı Kiralama</Link></li>
            </ul>
          </div>

          <div className="space-y-3">
            <h3 className="font-bold text-sm text-[#9EFF00] border-b-2 border-[#9EFF00]/30 pb-2">SEKTÖRLER</h3>
            <ul className="text-xs space-y-2.5 text-neutral-400 font-semibold">
              <li><Link href="/sektorel/kadin-giyim-reklam-ajansi" className="hover:underline hover:text-white transition-colors">Kadın Giyim</Link></li>
              <li><Link href="/sektorel/tesettur-giyim-dijital-pazarlama" className="hover:underline hover:text-white transition-colors">Tesettür Giyim</Link></li>
              <li><Link href="/sektorel/butikler-icin-instagram-reklamlari" className="hover:underline hover:text-white transition-colors">Butikler</Link></li>
              <li><Link href="/sektorel/kozmetik-eticaret-reklam-ajansi" className="hover:underline hover:text-white transition-colors">Kozmetik</Link></li>
              <li><Link href="/sektorel/taki-aksesuar-reklam-ajansi" className="hover:underline hover:text-white transition-colors">Takı &amp; Aksesuar</Link></li>
              <li><Link href="/sektorel/ayakkabi-canta-eticaret-reklamlari" className="hover:underline hover:text-white transition-colors">Ayakkabı &amp; Çanta</Link></li>
              <li><Link href="/sektorel/ev-dekorasyon-eticaret-reklamlari" className="hover:underline hover:text-white transition-colors">Ev &amp; Dekorasyon</Link></li>
              <li><Link href="/sektorel/e-ticaret-google-ads-ajansi" className="hover:underline hover:text-white transition-colors">Google Ads (E-Ticaret)</Link></li>
            </ul>
          </div>

          <div className="space-y-3">
            <h3 className="font-bold text-sm text-[#9EFF00] border-b-2 border-[#9EFF00]/30 pb-2">KEŞFEDİN</h3>
            <ul className="text-xs space-y-2.5 text-neutral-400 font-semibold">
              <li><Link href="/kurumsal" className="hover:underline hover:text-white transition-colors">Hakkımızda &amp; Ekibimiz</Link></li>
              <li><Link href="/hizmetlerimiz" className="hover:underline hover:text-white transition-colors">Tüm Hizmetlerimiz</Link></li>
              <li><Link href="/referanslar" className="hover:underline hover:text-white transition-colors">Başarı Hikayeleri</Link></li>
              <li><Link href="/fiyatlar" className="hover:underline hover:text-white transition-colors">Hizmet Paketleri</Link></li>
              <li><Link href="/sss" className="hover:underline hover:text-white transition-colors">Sıkça Sorulan Sorular</Link></li>
              <li><Link href="/blog" className="hover:underline hover:text-white transition-colors">E-Ticaret &amp; SEO Blog</Link></li>
              <li><Link href="/roi-hesaplayici" className="hover:underline hover:text-white transition-colors">ROI Hesaplayıcı</Link></li>
              <li><Link href="/sozluk" className="hover:underline hover:text-white transition-colors">Dijital Sözlük</Link></li>
              <li><Link href="/randevu" className="hover:underline hover:text-white transition-colors">Randevu Al</Link></li>
            </ul>
          </div>

          <div className="space-y-3">
            <h3 className="font-bold text-sm text-[#9EFF00] border-b-2 border-[#9EFF00]/30 pb-2">YASAL</h3>
            <ul className="text-xs space-y-2.5 text-neutral-400 font-semibold">
              <li><Link href="/kvkk-aydinlatma-metni" className="hover:underline hover:text-white transition-colors">KVKK Aydınlatma Metni</Link></li>
              <li><Link href="/gizlilik-politikasi" className="hover:underline hover:text-white transition-colors">Gizlilik Politikası</Link></li>
              <li><Link href="/cerez-politikasi" className="hover:underline hover:text-white transition-colors">Çerez Politikası</Link></li>
              <li><button type="button" onClick={tercihPaneliniAc} className="hover:underline hover:text-white transition-colors text-left">Çerez Tercihleri</button></li>
              <li><Link href="/kullanim-kosullari" className="hover:underline hover:text-white transition-colors">Kullanım Koşulları</Link></li>
              <li><Link href="/hizmet-sozlesmesi" className="hover:underline hover:text-white transition-colors">Hizmet Sözleşmesi</Link></li>
              <li><Link href="/iptal-ve-iade-politikasi" className="hover:underline hover:text-white transition-colors">İptal ve İade Politikası</Link></li>
              <li><Link href="/site-haritasi" className="hover:underline hover:text-white transition-colors">Site Haritası</Link></li>
            </ul>
          </div>

          <div className="space-y-3 col-span-2 md:col-span-1">
            <h3 className="font-bold text-sm text-[#9EFF00] border-b-2 border-[#9EFF00]/30 pb-2">İLETİŞİM</h3>
            <ul className="text-xs space-y-2.5 font-mono text-neutral-300">
              <li><a href="mailto:admin@dijipo.com" className="hover:text-[#9EFF00] transition-colors">admin@dijipo.com</a></li>
              <li><a href="tel:+905320522545" onClick={() => trackEvent('contact', { event_label: 'footer_tel' })} className="hover:text-[#9EFF00] transition-colors">0532 052 25 45</a></li>
              <li><a href="https://wa.me/905320522545" target="_blank" rel="noopener noreferrer" onClick={() => trackEvent('contact', { event_label: 'footer_whatsapp' })} className="hover:text-[#9EFF00] transition-colors">WhatsApp'tan Yazın</a></li>
            </ul>
            <p className="text-xs font-medium text-neutral-400 leading-relaxed pt-1">
              Atatürk Mah. Ertuğrul Gazi Sk.<br />Metropol İstanbul C1 Blok<br />No:2B, D:101<br />34700 Ataşehir/İstanbul
            </p>
          </div>

        </div>

        {/* Popüler aramalar — SEO açılış sayfalarına iç bağlantı şeridi */}
        <div className="max-w-7xl mx-auto border-t border-neutral-800 mt-8 pt-6 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs font-semibold text-neutral-400">
          <span className="font-mono uppercase tracking-wider text-[#9EFF00]">Popüler Aramalar:</span>
          {[
            ["/e-ticaret-reklam-ajansi", "E-Ticaret Reklam Ajansı"],
            ["/google-ads-ajansi", "Google Ads Ajansı"],
            ["/meta-reklam-ajansi", "Meta Reklam Ajansı"],
            ["/performans-pazarlama-ajansi", "Performans Pazarlama Ajansı"],
            ["/e-ticaret-danismanligi", "E-Ticaret Danışmanlığı"],
            ["/dijital-pazarlama-ajansi", "Dijital Pazarlama Ajansı"],
            ["/ikas-reklam-ajansi", "ikas Reklam Ajansı"],
            ["/ikas-danismanligi", "ikas Danışmanlığı"],
            ["/yapay-zeka-reklam-ajansi", "Yapay Zeka Reklam Ajansı"],
            ["/chatgpt-reklam-ajansi", "ChatGPT Reklam Ajansı"],
            ["/tiktok-reklam-ajansi", "TikTok Reklam Ajansı"],
            ["/e-ticaret-site-kurulumu", "E-Ticaret Site Kurulumu"],
            ["/dijipo-x-madrid-tema", "Dijipo x Madrid Teması"],
            ["/e-ticaret-altyapi-gecisi", "E-Ticaret Altyapı Geçişi"],
            ["/pixel-kayip-hesaplayici", "Pixel Kayıp Hesaplayıcı"],
            ["/tesettur-sezon-takvimi", "Tesettür Sezon Takvimi"],
            ["/ucretsiz-meta-hesap-denetimi", "Ücretsiz Meta Hesap Denetimi"],
            ["/e-ticaret-seo-ajansi", "E-Ticaret SEO Ajansı"],
            ["/istanbul-eticaret-reklam-ajansi", "İstanbul E-Ticaret Reklam Ajansı"],
            ["/izmir-eticaret-reklam-ajansi", "İzmir E-Ticaret Reklam Ajansı"],
            ["/ankara-eticaret-reklam-ajansi", "Ankara E-Ticaret Reklam Ajansı"],
            ["/antalya-eticaret-reklam-ajansi", "Antalya E-Ticaret Reklam Ajansı"],
            ["/bursa-eticaret-reklam-ajansi", "Bursa E-Ticaret Reklam Ajansı"],
          ].map(([href, etiket]) => (
            <Link key={href} href={href} className="hover:text-white transition-colors">{etiket}</Link>
          ))}
        </div>

        <div className="max-w-7xl mx-auto border-t border-neutral-800 mt-6 pt-6 flex flex-col md:flex-row items-center justify-between gap-4 text-xs text-neutral-400 font-semibold">
          <span>&copy; 2026 Dijipo Dijital Pazarlama Ajansı. Tüm hakları saklıdır.</span>
          <span>Shopify ve İkas kendi şirketlerinin tescilli markalarıdır. Dijipo bağımsız bir resmi iş ortağıdır.</span>
        </div>
      </footer>

      {/* ÜCRETSİZ ANALİZ AL MODAL */}
      {isOfferOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-xs overflow-y-auto">
          <div className="relative bg-white border-4 border-black w-full max-w-md shadow-[8px_8px_0px_0px_rgba(0,0,0,1)] p-6 space-y-4 max-h-[95vh] overflow-y-auto text-black">
            
            <button 
              onClick={() => { setIsOfferOpen(false); resetForm(); }}
              className="absolute top-4 right-4 bg-white border-2 border-black p-1 shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] active:translate-x-[1px] active:translate-y-[1px] active:shadow-none transition-all cursor-pointer"
            >
              <X className="h-4 w-4" />
            </button>

            {!isSuccess ? (
              <>
                <h3 className="font-black text-xl">Ücretsiz Mağaza Analizi Alın</h3>
                <p className="text-xs text-neutral-600 font-semibold">
                  Mevcut e-ticaret sitenizi veya reklam hesaplarınızı ücretsiz analiz edelim, büyüme fırsatlarını raporlayalım.
                </p>

                <form onSubmit={handleSendOffer} className="space-y-4">
                  <div className="space-y-1">
                    <label className="text-xs font-bold block">ADINIZ SOYADINIZ</label>
                    <input 
                      type="text" 
                      value={clientName}
                      onChange={(e) => setClientName(e.target.value)}
                      placeholder="Örn: Ahmet Yılmaz"
                      className="w-full border-2 border-black p-2 text-sm focus:outline-none"
                      required
                    />
                  </div>
                  <div className="space-y-1">
                    <label className="text-xs font-bold block">E-POSTA ADRESİNİZ</label>
                    <input 
                      type="email" 
                      value={clientEmail}
                      onChange={(e) => setClientEmail(e.target.value)}
                      placeholder="ahmet@sirketiniz.com"
                      className="w-full border-2 border-black p-2 text-sm focus:outline-none"
                      required
                    />
                  </div>
                  <div className="space-y-1">
                    <label className="text-xs font-bold block">TELEFON NUMARANIZ</label>
                    <input 
                      type="tel" 
                      value={clientPhone}
                      onChange={(e) => setClientPhone(e.target.value)}
                      placeholder="Örn: 0532 XXX XX XX"
                      className="w-full border-2 border-black p-2 text-sm focus:outline-none"
                    />
                  </div>
                  <div className="space-y-1">
                    <label className="text-xs font-bold block">E-TİCARET ALTYAPINIZ</label>
                    <select 
                      value={clientPlatform}
                      onChange={(e) => setClientPlatform(e.target.value)}
                      className="w-full border-2 border-black p-2 text-sm focus:outline-none font-bold"
                    >
                      <option value="Shopify">Shopify</option>
                      <option value="ikas">ikas</option>
                      <option value="Diğer">Diğer / Bilmiyorum</option>
                    </select>
                  </div>
                  <div className="space-y-1">
                    <label className="text-xs font-bold block">MESAJINIZ (OPSİYONEL)</label>
                    <textarea 
                      value={clientMessage}
                      onChange={(e) => setClientMessage(e.target.value)}
                      placeholder="E-ticaret sitenizin adresi ve hedefleriniz..."
                      rows={3}
                      className="w-full border-2 border-black p-2 text-sm focus:outline-none"
                    />
                  </div>

                  <button 
                    type="submit"
                    className="w-full bg-[#9EFF00] text-black border-2 border-black py-3 font-bold text-sm shadow-[3px_3px_0px_0px_rgba(0,0,0,1)] active:translate-x-[1px] active:translate-y-[1px] active:shadow-none transition-all cursor-pointer"
                  >
                    Ücretsiz Analiz Talebini Gönder
                  </button>
                </form>
              </>
            ) : (
              <div className="text-center py-8 space-y-4 animate-zoom-in">
                <div className="w-16 h-16 bg-[#9EFF00] border-4 border-black flex items-center justify-center mx-auto shadow-[3px_3px_0px_0px_rgba(0,0,0,1)]">
                  <CheckCircle2 className="w-10 h-10 text-black" />
                </div>
                <div className="space-y-1">
                  <h3 className="text-xl font-bold">Analiz Talebiniz Alındı!</h3>
                  <p className="text-xs text-neutral-600 font-semibold max-w-xs mx-auto">
                    Uzman ekibimiz web sitenizi ve reklam hesaplarınızı inceleyerek 24 saat içinde size özel büyüme raporu hazırlayacaktır.
                  </p>
                </div>
                <button 
                  onClick={() => { setIsOfferOpen(false); resetForm(); }}
                  className="bg-black text-white border-2 border-black px-6 py-2 font-bold text-xs shadow-[3px_3px_0px_0px_rgba(158,255,0,1)] active:translate-x-[1px] active:translate-y-[1px] active:shadow-none transition-all"
                >
                  Kapat
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* WHATSAPP CANLI DESTEK WIDGET — çerez paneli açıkken lg altında gizlenir (sohbet-araci, index.css) */}
      <div className="sohbet-araci fixed bottom-6 right-6 z-50 flex flex-col items-end">
        {isWhatsAppOpen && (
          <div className="mb-4 bg-white border-4 border-black w-72 md:w-80 shadow-[6px_6px_0px_0px_rgba(0,0,0,1)] p-5 space-y-4 animate-zoom-in text-black">
            <div className="flex items-center justify-between border-b-2 border-black pb-2">
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-[#9EFF00] rounded-full animate-pulse border border-black"></div>
                <span className="font-bold text-sm">Dijipo Canlı Destek</span>
              </div>
              <button 
                onClick={() => setIsWhatsAppOpen(false)}
                className="hover:text-neutral-600 transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            
            <p className="text-xs text-neutral-600 font-semibold">
              Merhaba! Shopify veya İkas sitenizin ciro büyümesi ve reklam hesapları hakkında sormak istediklerinizi yazıp hemen WhatsApp üzerinden bizimle sohbete başlayabilirsiniz.
            </p>

            <form onSubmit={handleSendWhatsApp} className="space-y-3">
              <div className="space-y-1">
                <label className="text-[10px] font-bold block font-mono">MESAJINIZ</label>
                <textarea 
                  value={whatsappMessage}
                  onChange={(e) => setWhatsAppMessage(e.target.value)}
                  placeholder="E-ticaret sitemiz için destek almak istiyoruz..."
                  rows={3}
                  className="w-full border-2 border-black p-2 text-xs focus:outline-none font-medium"
                  required
                />
              </div>

              <button 
                type="submit"
                className="w-full bg-[#9EFF00] text-black border-2 border-black py-2 font-bold text-xs shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] active:translate-x-[1px] active:translate-y-[1px] active:shadow-none transition-all cursor-pointer flex items-center justify-center gap-1"
              >
                <MessageCircle className="w-4 h-4" /> WhatsApp ile Gönder
              </button>
            </form>
          </div>
        )}

        <button 
          onClick={() => setIsWhatsAppOpen(!isWhatsAppOpen)}
          aria-label={isWhatsAppOpen ? "Sohbet penceresini kapat" : "WhatsApp ile yazın"}
          aria-expanded={isWhatsAppOpen}
          className="bg-black text-[#9EFF00] border-3 border-black p-4 rounded-full shadow-[4px_4px_0px_0px_rgba(158,255,0,1)] hover:translate-x-[1px] hover:translate-y-[1px] hover:shadow-[2px_2px_0px_0px_rgba(158,255,0,1)] transition-all cursor-pointer flex items-center justify-center"
        >
          <MessageCircle className="w-6 h-6 text-[#9EFF00]" />
        </button>
      </div>



    </div>
  );
}
